import React, { FC, useState } from 'react'
import { Modal } from 'react-bootstrap'
import PassengerLuggageCounter from '../../components/FormCounter'
import {
  LuggageRequirements,
  LuggageTypes,
  OversizeLuggageRequirements,
  OversizeLuggageTypes,
  PassengerRequirements,
  PassengerTypes,
} from '../../types'
import LuggageTag from '../../components/LuggageTag'
import PassengersTag from '../../components/PassengersTag'
import OversizedLuggageTag from '../../components/OversizedLuggageTag'

interface Props {
  passengers: PassengerRequirements
  luggage: LuggageRequirements
  oversized: OversizeLuggageRequirements
  onChange?: (requirements: PassengerRequirements & LuggageRequirements & OversizeLuggageRequirements) => void
}

const PassengersAndLuggage: FC<Props> = ({ onChange, passengers, luggage, oversized }) => {
  const { adults, children, infants } = passengers
  const { luggage_small, luggage_large } = luggage
  const totalOversized = Object.values(oversized).reduce((curr, prev) => curr + prev, 0)
  const totalLuggage = luggage_small + luggage_large
  const totalPassengers = adults + children + infants

  const [hasOversizeLuggage, setHasOversizeLuggage] = useState(totalOversized > 0)
  const [requirements, setRequirements] = useState({
    ...passengers,
    ...luggage,
    ...oversized,
  })
  const [modalVisible, setModalVisible] = useState(false)

  const updateRequirements = (key: PassengerTypes | LuggageTypes | OversizeLuggageTypes, count: number) => {
    setRequirements({
      ...requirements,
      [key]: count,
    })
  }

  const toggleModal = () => {
    setModalVisible(!modalVisible)
    setRequirements({ ...passengers, ...luggage, ...oversized })
    setHasOversizeLuggage(totalOversized > 0)
  }

  const submit = () => {
    onChange?.({ ...requirements })
    setModalVisible(false)
  }

  return (
    <div className="booking-input-single booking-input-passengers mb-4 position-relative">
      <div className="passengers-left-icon position-absolute">
        <span className="icon-car-small"></span>
      </div>
      <div className="passengers-wrap form-control d-flex align-items-center">
        {totalLuggage === 0 && totalPassengers === 0 && totalOversized === 0 ? (
          <span className="placeholder-text text-secondary">Passengers and luggage</span>
        ) : null}
        <PassengersTag requirements={passengers} />
        <LuggageTag requirements={luggage} />
        <OversizedLuggageTag requirements={oversized} />
      </div>

      <button
        type="button"
        className="passengers-edit text-secondary position-absolute bg-light-blue p-1 btn btn-link"
        onClick={toggleModal}
      >
        <span className="icon-pencil"></span>
      </button>

      <Modal show={modalVisible} onHide={toggleModal} centered>
        <Modal.Body>
          <div className="passengers-model-wrap p-3">
            <div className="passengers-model-content">
              <h5 className="mb-3">Passengers and luggage</h5>

              <PassengerLuggageCounter
                label="Adults"
                icon="users"
                count={requirements.adults}
                onChange={(c) => updateRequirements('adults', c)}
              />
              <PassengerLuggageCounter
                label="Children (<13 years)"
                icon="users"
                count={requirements.children}
                onChange={(c) => updateRequirements('children', c)}
              />
              <PassengerLuggageCounter
                label="Infants (<2 years)"
                icon="users"
                count={requirements.infants}
                onChange={(c) => updateRequirements('infants', c)}
              />

              <PassengerLuggageCounter
                label="Large luggage"
                icon="suitcase"
                count={requirements.luggage_large}
                onChange={(c) => updateRequirements('luggage_large', c)}
              />

              <PassengerLuggageCounter
                label="Small bags"
                icon="bag-small"
                count={requirements.luggage_small}
                onChange={(c) => updateRequirements('luggage_small', c)}
              />

              <div className="ps-row d-flex align-items-center py-3">
                <span className="ps-row-icon">
                  <span className="icon-luggage"></span>
                </span>
                <span className="flex-grow-1 pl-2">Oversize luggage?</span>
                <div className="ps-row-ctrls d-flex align-items-center">
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="has-oversize-luggage"
                      onChange={() => setHasOversizeLuggage(!hasOversizeLuggage)}
                      checked={hasOversizeLuggage}
                    />
                    <label className="custom-control-label" htmlFor="has-oversize-luggage">
                      {hasOversizeLuggage ? 'Yes' : 'No'}
                    </label>
                  </div>
                </div>
              </div>

              {hasOversizeLuggage && (
                <>
                  <PassengerLuggageCounter
                    label="Child seats"
                    count={requirements.oversized_child_seats}
                    onChange={(c) => updateRequirements('oversized_child_seats', c)}
                  />
                  <PassengerLuggageCounter
                    label="Golf clubs"
                    count={requirements.oversized_golf_clubs}
                    onChange={(c) => updateRequirements('oversized_golf_clubs', c)}
                  />
                  <PassengerLuggageCounter
                    label="Prams"
                    count={requirements.oversized_prams}
                    onChange={(c) => updateRequirements('oversized_prams', c)}
                  />
                  <PassengerLuggageCounter
                    label="Skis"
                    count={requirements.oversized_skis}
                    onChange={(c) => updateRequirements('oversized_skis', c)}
                  />
                  <PassengerLuggageCounter
                    label="Snowboards"
                    count={requirements.oversized_snowboards}
                    onChange={(c) => updateRequirements('oversized_snowboards', c)}
                  />
                </>
              )}
            </div>
            <button type="button" className="btn btn-secondary btn-lg btn-block rounded-0 mt-4" onClick={submit}>
              Done
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default PassengersAndLuggage
